import { Connection } from '@/types/connection';
import { ValidationError } from '@/types/validation';
import { isNumber } from '@/util/typeGuards';

const connectionKeys = ['action', 'label', 'dst'] as const;

// A row is blank while its action is being typed, so the position is what is left to name it by.
const connectionName = (connection: Connection, index: number) =>
  connection.action || connection.label || `#${index + 1}`;

/**
 * Every entry carries an action, a label and a dst that is a block id or null. An unconnected action
 * without a dst runs the same, but the session trace loses the label of the action that ended the task.
 */
export const validateConnections = (
  connections: Connection[],
  nodeId: number,
  blockName: string
): ValidationError[] => {
  const errors = connections.flatMap((connection, index) => {
    const name = connectionName(connection, index);
    const entryErrors: ValidationError[] = connectionKeys
      .filter((key) => !(key in connection))
      .map((key) => ({
        severity: 'error',
        message: `Block ${blockName} has a connection (${name}) that carries no ${key}.`,
        id: nodeId
      }));

    if ('dst' in connection && !isNumber(connection.dst) && connection.dst !== null) {
      entryErrors.push({
        severity: 'error',
        message: `Block ${blockName} has a connection (${name}) whose dst is neither a block id nor null.`,
        id: nodeId
      });
    }

    return entryErrors;
  });

  errors.push(...validateUniqueActions(connections, nodeId, blockName));
  return errors;
};

const validateUniqueActions = (connections: Connection[], nodeId: number, blockName: string): ValidationError[] => {
  const actions = connections.reduce((groups, connection) => {
    if (!connection.action) return groups;

    const key = connection.action.toLowerCase();
    const group = groups.get(key) ?? [];

    group.push(connection.action);
    groups.set(key, group);

    return groups;
  }, new Map<string, string[]>());

  return [...actions.values()]
    .filter((group) => group.length > 1)
    .map((group) => ({
      severity: 'error',
      message: `Block ${blockName} has connections (${group.join(', ')}) that share the same action, ignoring case.`,
      id: nodeId
    }));
};
